'use client'

import { CaretDownIcon } from '@/components/icons/CaretDownIcon'
import { CheckmarkIcon } from '@/components/icons/CheckmarkIcon'
import { GlobeIcon } from '@/components/icons/GlobeIcon'
import { usePathname, useRouter } from '@/i18n/navigation'
import { cn } from '@/lib/utils'
import { Locale, useLocale, useTranslations } from 'next-intl'
import { useParams } from 'next/navigation'
import { DropdownMenu } from 'radix-ui'
import { useTransition } from 'react'

const languageOptions = [
  { value: 'hy', label: 'Հայ' },
  { value: 'en', label: 'Eng' },
  { value: 'ru', label: 'Рус' }
]

export function LanguageSwitcher() {
  const [isPending, startTransition] = useTransition()
  const router = useRouter()
  const pathname = usePathname()
  const params = useParams()

  const t = useTranslations('LanguageSwitcher')
  const locale = useLocale()

  function onSelectChange(value: string) {
    const nextLocale = value as Locale
    startTransition(() => {
      // Preserve the hash fragment from the current URL
      const currentHash = window.location.hash

      router.replace(
        // @ts-expect-error -- TypeScript will validate that only known `params`
        // are used in combination with a given `pathname`. Since the two will
        // always match for the current route, we can skip runtime checks.
        { pathname: pathname + currentHash, params },
        { locale: nextLocale, scroll: false }
      )
    })
  }

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger disabled={isPending} asChild>
        <button
          type="button"
          className="border-primary-600 text-primary-500 flex w-[100px] cursor-pointer items-center gap-2 rounded-3xl border bg-white px-3 py-2.5"
          aria-label="Select language"
        >
          <GlobeIcon size="18px" />
          <span className="text-body-3">{t('locale', { locale })}</span>
          <CaretDownIcon size="20px" className="ml-auto" />
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className="content border-primary-600 z-10 w-[var(--radix-dropdown-menu-trigger-width)] rounded-2xl border bg-white p-3"
          sideOffset={15}
        >
          <DropdownMenu.RadioGroup
            className="space-y-3"
            onValueChange={onSelectChange}
          >
            {languageOptions.map((option) => (
              <DropdownMenu.RadioItem
                key={option.value}
                value={option.value}
                className={cn(
                  'text-primary-500 flex cursor-pointer items-center gap-2 rounded-lg px-1.5 py-1.5 transition-all',
                  'hover:bg-gray-50',
                  locale === option.value && 'bg-gray-50'
                )}
              >
                {option.label}

                {locale === option.value && (
                  <span className="ml-auto">
                    <CheckmarkIcon size="20px" />
                  </span>
                )}
              </DropdownMenu.RadioItem>
            ))}
          </DropdownMenu.RadioGroup>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}
